import type { AudioFrame } from './audio.js'
import { decodeAudioFrame } from './audio.js'
import type { ClientMessage, ServerMessage } from './messages.js'
import { clientMessage, serverMessage } from './messages.js'
import { parse } from './shape.js'

/**
 * Turning bytes on a socket into something the program can switch on.
 *
 * Both ends need exactly this, and if each writes its own then the two
 * disagree about what counts as a valid frame, which is a class of bug that
 * only shows up between versions and only in production. So it lives here.
 *
 * Nothing in this file throws. Every function that touches network input
 * returns a value describing what it found, including the bad cases, because
 * a message loop that has to be wrapped in a try to be safe eventually gets
 * called from somewhere that forgot to.
 */

/**
 * The biggest text frame either side will read, in characters.
 *
 * Audio does not travel as text, so nothing legitimate comes close: the
 * largest real message is a transcript.
 *
 * This is the second line of defence, not the first. The real ceiling is the
 * WebSocket implementation's own payload limit, which rejects an oversized
 * frame while it is still bytes on a socket and never allocates a string for
 * it at all. By the time a frame reaches this function the string exists, so
 * what the check actually prevents is handing it to `JSON.parse`, which is
 * where a hostile frame turns one megabyte of text into a hundred megabytes of
 * objects.
 *
 * Counted in UTF-16 code units rather than encoded bytes, because measuring
 * the bytes means encoding the whole string to find out, which is the same
 * allocation the limit exists to avoid. A string is never fewer bytes than it
 * has code units, so a string that passes this is at most three times the
 * ceiling on the wire, and that is a bound worth having cheaply.
 */
export const MAX_TEXT_FRAME_BYTES = 128 * 1024

/** Anything a WebSocket implementation might hand over as a received frame. */
export type RawFrame = string | ArrayBuffer | ArrayBufferView

export type DecodedClientFrame =
  | { kind: 'message'; message: ClientMessage }
  | { kind: 'audio'; frame: AudioFrame }
  | { kind: 'invalid'; reason: string }

export type DecodedServerFrame =
  | { kind: 'message'; message: ServerMessage }
  | { kind: 'audio'; frame: AudioFrame }
  | { kind: 'invalid'; reason: string }

function asBytes(frame: ArrayBuffer | ArrayBufferView): Uint8Array {
  if (frame instanceof ArrayBuffer) return new Uint8Array(frame)
  return new Uint8Array(frame.buffer, frame.byteOffset, frame.byteLength)
}

function decodeText(text: string): { ok: true; value: unknown } | { ok: false; reason: string } {
  if (text.length > MAX_TEXT_FRAME_BYTES) {
    return { ok: false, reason: `text frame is ${text.length} characters, over the limit` }
  }
  try {
    return { ok: true, value: JSON.parse(text) as unknown }
  } catch {
    return { ok: false, reason: 'text frame is not valid JSON' }
  }
}

/** Reads a frame the server received from a client. */
export function decodeClientFrame(frame: RawFrame): DecodedClientFrame {
  if (typeof frame !== 'string') {
    const decoded = decodeAudioFrame(asBytes(frame))
    return decoded.ok
      ? { kind: 'audio', frame: decoded.frame }
      : { kind: 'invalid', reason: decoded.reason }
  }

  const text = decodeText(frame)
  if (!text.ok) return { kind: 'invalid', reason: text.reason }

  const result = parse(clientMessage, text.value)
  return result.ok
    ? { kind: 'message', message: result.value }
    : { kind: 'invalid', reason: result.message }
}

/** Reads a frame the client received from the server. */
export function decodeServerFrame(frame: RawFrame): DecodedServerFrame {
  if (typeof frame !== 'string') {
    const decoded = decodeAudioFrame(asBytes(frame))
    return decoded.ok
      ? { kind: 'audio', frame: decoded.frame }
      : { kind: 'invalid', reason: decoded.reason }
  }

  const text = decodeText(frame)
  if (!text.ok) return { kind: 'invalid', reason: text.reason }

  const result = parse(serverMessage, text.value)
  return result.ok
    ? { kind: 'message', message: result.value as ServerMessage }
    : { kind: 'invalid', reason: result.message }
}

export function encodeClientMessage(message: ClientMessage): string {
  return JSON.stringify(message)
}

export function encodeServerMessage(message: ServerMessage): string {
  return JSON.stringify(message)
}
